import React, { useState, useEffect, useMemo, Fragment } from 'react';

import {
  Box,
  Typography
} from '@mui/material';
import {
  LineChart, Line,
  XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
  ResponsiveContainer, ReferenceLine, Customized
} from 'recharts';

function MonthlyChartComponent({chartRef, title, data, exportMode, showPinnedBox=false}) {

    const LINE_PALETTE = ['#1f77b4','#ff7f0e','#2ca02c','#d62728'];
    const FORECAST_COLOR = '#2e7d32';
    const BUDGET_COLOR = '#9c27b0';

    const [asOf, setAsOf] = React.useState('');
    const [metric, setMetric] = React.useState('value');
    const [monthlyData, setMonthlyData] = useState([]);
    const isCash = metric === 'value';
    const ordinal = (n) => { const s=['th','st','nd','rd'], v=n%100; return n+(s[(v-20)%10]||s[v]||s[0]); };
    const fmtGBP = (n) => `£${Math.round(n||0).toLocaleString('en-GB')}`;
    const fmtUnits = (n) => `${Math.round(n||0).toLocaleString('en-GB')}`;
    const labelFmt = (iso) => {
        try {
        const d = new Date(iso + 'T00:00:00');
        return d.toLocaleDateString('en-GB', { month: 'short', day: '2-digit' });
        } catch { return iso; }
    };
    
    useEffect(() => { 
        if (!data || data.length == 0) return;
        setAsOf(data.meta.observed_through);
        const parsed = Array.isArray(data.points) ? data.points.map(p => { const day = parseInt((p.date || '').split('-')[2] || '0', 10);
            return { day, current: p.current, avg2: p.avg2, avg3: p.avg3, avg6: p.avg6, date: p.date, eom_forecast: p.eom_forecast, budget_flat: p.budget_flat};
        }) : [];
        setMonthlyData(parsed);
        setMetric(data.metric);
    }, [data]);
    
    const monthName = React.useMemo(() => {
        const d = asOf ? new Date(asOf + 'T00:00:00') : new Date();
        return d.toLocaleString('en-GB', { month: 'long' });
    }, [asOf]);

    const LABELS = [
        `Cumulative Daily Sales (${monthName})`,
        'Average last 2 months sales',
        'Average last 3 months sales',
        'Average last 6 months sales',
        'EOM Forecast predicted by RxAI',
        `${monthName} Budget`
    ];

    const NAME_MAP = {
        budget_flat: `${monthName} Budget`,
        current: `Cumulative Daily Sales (${monthName})`,
        eom_forecast: `Forecast predicted by RxAI`,
        avg2: 'Average last 2 months sales',
        avg3: 'Average last 3 months sales',
        avg6: 'Average last 6 months sales'
    };
    const CHART_HEIGHT = 420;

    const todayMonthEntry = React.useMemo(() => {
        const byDate = monthlyData.find(p => p.date === asOf);
        return byDate || monthlyData[monthlyData.length - 1] || null;
    }, [monthlyData, asOf]);

    // Resolve the index for todayMonthEntry (fallback to last point)
    const selectedIndex = useMemo(() => {
        if (!monthlyData?.length) return 0;
        if (todayMonthEntry != null) {
        const i = monthlyData.findIndex(d => Number(d.day) === Number(todayMonthEntry.day));
        if (i >= 0) return i;
        }
        return monthlyData.length - 1;
    }, [monthlyData, todayMonthEntry]);

    const selected = monthlyData?.[selectedIndex];

    const rows = [
        { key: 'current',       color: LINE_PALETTE[0], label: LABELS[0],     value: selected?.current },
        { key: 'budget_flat',   color: BUDGET_COLOR,    label: LABELS[5],     value: selected?.budget_flat },
        { key: 'avg2',          color: LINE_PALETTE[1], label: LABELS[1],     value: selected?.avg2 },
        { key: 'avg3',          color: LINE_PALETTE[2], label: LABELS[2],     value: selected?.avg3 },
        { key: 'avg6',          color: LINE_PALETTE[3], label: LABELS[3],     value: selected?.avg6 },
    ];

    const stats = React.useMemo(() => {
        if (!monthlyData.length) return null;
        const last = monthlyData[monthlyData.length - 1];
        let latestCur = null;
        for (let i = monthlyData.length - 1; i >= 0; i--) {
        if (monthlyData[i].current != null && !Number.isNaN(monthlyData[i].current)) { latestCur = monthlyData[i].current; break; }
        }
        return { current: latestCur || 0, avg2: last?.avg2 || 0, avg3: last?.avg3 || 0, avg6: last?.avg6 || 0, eom_forecast: last?.eom_forecast || 0, budget_flat: last?.budget_flat || 0  };
    }, [monthlyData]);

    return (
        <Box ref={chartRef} sx={{ width: exportMode ? 1000 : '100%' }} padding={2}>
        <Typography variant="subtitle2" sx={{ fontWeight:500 }}>
            {title}
        </Typography>
        {stats && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 1.5 }}>
            {[
                { color: LINE_PALETTE[0], label: LABELS[0], value: stats.current },
                { color: BUDGET_COLOR, label: LABELS[5], value: stats.budget_flat },
                // { color: FORECAST_COLOR, label: LABELS[4], value: stats.eom_forecast },
                { color: LINE_PALETTE[1], label: LABELS[1], value: stats.avg2 },
                { color: LINE_PALETTE[2], label: LABELS[2], value: stats.avg3 },
                { color: LINE_PALETTE[3], label: LABELS[3], value: stats.avg6 },
            ].map((r, i) => (
                <Box
                key={i}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.25,
                    py: 0.75,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 999,
                    bgcolor: 'background.paper',
                    minWidth: 0,
                    maxWidth: '100%',
                }}
                title={`${r.label}: ${isCash ? fmtGBP(r.value) : fmtUnits(r.value)}`}
                >
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: r.color, flex: '0 0 auto' }} />
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
                    {r.label}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {isCash ? fmtGBP(r.value) : fmtUnits(r.value)}
                </Typography>
                </Box>
            ))}
            </Box>
        )}
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <LineChart data={monthlyData} margin={{ top: 8, right: 12, bottom: 8, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="day" tickFormatter={(d)=>ordinal(Number(d))} tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v)=> isCash ? fmtGBP(v) : fmtUnits(v)} tick={{ fontSize: 12 }} />
            <RTooltip
                formatter={(val, name, props) => {
                const nice = NAME_MAP[props.dataKey] || name;
                return [isCash ? fmtGBP(val) : fmtUnits(val), nice];
                }}
                labelFormatter={(lbl)=> `Day ${ordinal(Number(lbl))}`}
            />
            <Line type="monotone" dataKey="current" stroke={LINE_PALETTE[0]} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="budget_flat" stroke={BUDGET_COLOR} strokeDasharray="6 4" strokeWidth={2.25} dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="eom_forecast" stroke={FORECAST_COLOR} strokeDasharray="6 4" strokeWidth={2.25} dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="avg2" stroke={LINE_PALETTE[1]} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="avg3" stroke={LINE_PALETTE[2]} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="avg6" stroke={LINE_PALETTE[3]} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            {exportMode && showPinnedBox && selected && (
                <ReferenceLine
                x={selected.day}
                stroke="#999"
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
                />
            )}
            {exportMode && showPinnedBox && selected && (
                <Customized
                component={
                    <PinnedTooltipBox
                    point={selected}
                    metric={metric}
                    fmtGBP={fmtGBP}
                    fmtUnits={fmtUnits}
                    rows={rows}
                    />
                }
                />
            )}
            </LineChart>
        </ResponsiveContainer>
        <Typography variant="subtitle2" sx={{ fontWeight:500 }}>
            EOM Summary generated by RxAI
        </Typography>
        </Box>
    );

    function PinnedTooltipBox({ point, metric, fmtGBP, fmtUnits, rows, ...ctx }) {
        const { offset, xAxisMap } = ctx;
        const xAxis = Object.values(xAxisMap)[0];
        const anchorX = Math.round(xAxis?.scale?.(point.day) ?? 0) + (offset?.left ?? 0);

        const boxWidth = 300;
        const boxHeight = 110;
        const padding = 8;

        let boxX = anchorX + 60;
        const chartRight = (offset?.left ?? 0) + (ctx?.chartWidth ?? 0);
        if (boxX + boxWidth + padding > chartRight) boxX = anchorX - 60 - boxWidth;
        const boxY = (offset?.top ?? 0) + 12;
        const fmt = (v) => (metric === 'value' ? fmtGBP(v) : fmtUnits(v));

        return (
        <g>
            <foreignObject x={boxX} y={boxY} width={boxWidth} height="200">
            <div
                style={{
                background: 'var(--mui-palette-background-paper, #fff)',
                border: '1px solid var(--mui-palette-divider, #e0e0e0)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                borderRadius: 8,
                padding: 10,
                fontFamily: 'inherit',
                fontSize: 12,
                lineHeight: 1.4,
                }}
            >
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Day {ordinal(Number(point.day))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 6 }}>
                {rows.map(r => (
                    <Fragment key={r.key}>
                    <span style={{
                        width: 10, height: 10, borderRadius: 9999, background: r.color, display: 'inline-block', marginTop: 4
                    }} />
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.label}
                    </span>
                    <strong>{fmt(r.value)}</strong>
                    </Fragment>
                ))}
                </div>
            </div>
            </foreignObject>
        </g>
        );
    }
}

export default MonthlyChartComponent;